import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import WorksheetSelectBar from "./common/WorksheetSelectBar";
import WorksheetSearchBar from "./common/WorksheetSearchBar";

import { fetchAssignments } from "../assignmentListSlice";
import { fetchLevels, fetchStudents } from "../../student/studentSlice";
import { RETRY_SUPPORTED_TYPE_LIST } from "../../../utils/worksheetUtils";
import { 
  retrieveCommentaryPDFAPI, 
  createRetryAssignmentAPI, 
  retrieveVocabularyRetryPDFAPI,
  retrieveVocabularyWordbookPDFAPI
} from "../worksheetAPI";

import {
  formatDateString,
  showStudentURL,
} from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";
import secret from "../../../secret.json";

import LinkCopyButton from "../../../components/complex/LinkCopyButton";
import ReviewPageButton from "../../../components/complex/ReviewPageButton";
import CommentaryPDFButton from "../../../components/complex/CommentaryPDFButton";
import FilteringTab from "./common/FilteringTab/FilteringTab";
import StudentLevelTestHistory from "../../leveltest/components/StudentLevelTestHistory";
import Pagination from "../../../components/Pagination";
import WorksheetTypeBox from "./common/WorksheetTypeBox";
import CustomCheckbox from "../../../components/CustomCheckbox";
import SubmittedTime from "../../../components/SubmittedTime";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import BookIcon from "@mui/icons-material/Book";
import PhotoCameraIcon from "@mui/icons-material/PhotoCameraOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import RateReviewIcon from "@mui/icons-material/RateReviewOutlined";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import CorrectionModal from "./CorrectionModal";
import MathWorkPhotoModal, { WORK_PHOTO_RATING_LIST } from "./MathWorkPhotoModal";
import MathDrawingModal from "./MathDrawingModal";
import BrushIcon from "@mui/icons-material/BrushOutlined";
import DeleteWorksheetModal from "./common/DeleteWorksheetModal";
import PaperAssignmentListView from "./common/PaperAssignmentListView";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

const AssignmentReviewTable = () => {
  // [모바일 수정] 이 화면은 데스크톱 고정 폭(사이드바 250px + 표 85% + 8열
  // colgroup)이라 폰에서 열이 한 글자씩 세로로 붕괴했다(스크린샷 재현).
  // 폰에서는 ① 필터를 위로 쌓고 ② 표를 원래 폭 그대로 두되 가로 스와이프.
  const [isMobileW, setIsMobileW] = React.useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  React.useEffect(() => {
    const f = () => setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const dispatch = useDispatch();

  const { results, current_page, total_pages } = useSelector(
    (state) => state.assignments
  );
  const { levels, allStudents } = useSelector((state) => state.student);
  const { user } = useSelector((state) => state.auth);

  const [keyword, setKeyword] = useState("");
  const [typeSelect, setTypeSelect] = useState("all");
  const [selectedParams, setSelectedParams] = useState({
    levelId: null,
    studentId: null,
  });
  const [selectedAssignments, setSelectedAssignments] = useState([]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  useEffect(() => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(
      fetchAssignments({
        page: 1,
        type: typeParam,
        keyword: keyword,
        levelId: selectedParams.levelId,
        studentId: selectedParams.studentId,
        studentGrade: selectedParams.studentGrade,
      })
    );
    dispatch(fetchLevels());
    dispatch(fetchStudents());
  }, [dispatch, keyword, typeSelect, selectedParams]);

  const showCommentaryPDF = async (assignment) => {
    const pdfWindow = window.open();
    const response = await retrieveCommentaryPDFAPI(
      assignment.id,
      assignment.student.id
    );
    if (response.status !== 200) alert("해설지를 불러오는데 실패했습니다.");

    const file = new Blob([response.data], {
      type: "application/pdf",
    });
    const fileURL = URL.createObjectURL(file);
    pdfWindow.location.href = fileURL;
  };

  // 수학 틀린 문제 PDF(오답 문제지 · 해설지). 서버 api/assignment/math_pdf.py 가 누를 때 굽는다.
  // 선생님 세션(학원 id:random_code)으로 연다. 서버가 자기가 낸 과제인지 본다
  const openPdfWithSession = (path) => {
    const padded = String(user.session || "").replace(/-/g, "+").replace(/_/g, "/");
    const [userId, randomCode] = atob(padded + "===".slice((padded.length + 3) % 4)).split(":");
    window.open(`${secret.API_BASE_URL.replace(/\/+$/, "")}${path}&student_id=${userId}&random_code=${randomCode}`, "_blank");
  };
  const openMathWrongPdf = (assignment, kind) =>
    openPdfWithSession(`/api/v1/assignment/math/wrong-pdf?assignment_id=${assignment.id}&kind=${kind}`);
  // 영어 채점 해설지(동그라미 · 엑스). 서버 api/assignment/english_pdf.py
  const openEnglishExplainPdf = (assignment) =>
    openPdfWithSession(`/api/v1/assignment/english/explain-pdf?assignment_id=${assignment.id}`);
  // 수학 월말 레벨테스트 리포트 PDF(점수 · 레벨 · 학기 진단 · 오답 카드 · AI 글). 서버 api/assignment/math_level_report_pdf.py
  const openMathLevelReport = (assignment) =>
    openPdfWithSession(`/api/v1/assignment/math/level-report?assignment_id=${assignment.id}`);
  // 반별 지침(같은 시험을 본 같은 반 학생 결과를 모은 PDF). 반은 서버가 이 줄 학생의 반으로 고르고, 여럿이면 고르는 화면을 띄운다
  const openMathLevelClassReport = (assignment) =>
    openPdfWithSession(`/api/v1/assignment/math/level-class-report?assignment_id=${assignment.id}`);

  // 수학 풀이 사진 창(보기 · 정성 매기기). 매기면 이 줄의 아이콘 색만 바로 바꾼다
  const [workPhotoAssignment, setWorkPhotoAssignment] = useState(null);
  // AI 첨삭 고치기(리더스북 · 원서 워크북)
  const [correctionAssignment, setCorrectionAssignment] = useState(null);
  const [ratingById, setRatingById] = useState({});
  // 수학 그리기 채점 창. 매기면 이 줄의 안 매긴 수만 바로 바꾼다
  const [drawingAssignment, setDrawingAssignment] = useState(null);
  const [drawingPendingById, setDrawingPendingById] = useState({});

  const showReviewPage = (assignment) => {
    const reviewURL = `${secret.STUDENT_BASE_URL}/review?id=${assignment.id}&session=${user.session}`;
    window.open(reviewURL, "_blank");
  };

  const handleVocabularyRetryPDF = async (assignment) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveVocabularyRetryPDFAPI(
        assignment.id,
        assignment.student.id,
        "test"
      );

      if (response.status !== 200) {
        alert("재시험지 PDF를 생성하는데 실패했습니다.");
        pdfWindow.close();
        return;
      }

      const file = new Blob([response.data], {
        type: "application/pdf",
      });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Failed to generate vocabulary retry PDF:", error);
      alert("재시험지 PDF를 생성하는데 실패했습니다.");
    }
  };

  const handleVocabularyWordbookPDF = async (assignment) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveVocabularyWordbookPDFAPI(
        assignment.id,
        assignment.student.id
      );

      if (response.status !== 200) {
        alert("단어장 PDF를 생성하는데 실패했습니다.");
        pdfWindow.close();
        return;
      }

      const file = new Blob([response.data], {
        type: "application/pdf",
      });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Failed to generate vocabulary wordbook PDF:", error);
      alert("단어장 PDF를 생성하는데 실패했습니다.");
    }
  };

  const handleRetryAssignment = async (assignment) => {
    if (!RETRY_SUPPORTED_TYPE_LIST.includes(assignment.type)) {
      alert("이 유형은 복습을 지원하지 않습니다.");
      return;
    }

    if (!assignment.finished_at) {
      alert("완료되지 않은 과제는 복습을 생성할 수 없습니다.");
      return;
    }

    // Use assignment's max_retries if available, otherwise default to 3
    const maxRetries = assignment.retry_info?.max_retries ?? 3;

    if (window.confirm(`${assignment.title} 과제에 대한 복습을 생성하시겠습니까?`)) {
      try {
        const response = await createRetryAssignmentAPI(assignment.id, maxRetries);
        alert(`복습 문제지가 생성되었습니다. (${response.retry_count}/${response.max_retries})`);
        // Refresh the data to show the new retry assignment
        const typeParam = typeSelect === "all" ? null : typeSelect;
        dispatch(
          fetchAssignments({
            page: current_page,
            type: typeParam,
            keyword: keyword,
            levelId: selectedParams.levelId,
            studentId: selectedParams.studentId,
            studentGrade: selectedParams.studentGrade,
          })
        );
      } catch (error) {
        console.error("Failed to create retry assignment:", error);
        alert(error.response?.data?.error || "복습 문제지 생성에 실패했습니다.");
      }
    }
  };

  const handlePageClick = (page) => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(
      fetchAssignments({
        page,
        type: typeParam,
        keyword: keyword,
        levelId: selectedParams.levelId,
        studentId: selectedParams.studentId,
        studentGrade: selectedParams.studentGrade,
      })
    );
  };

  const handleCheckboxChange = (checked, assignment) => {
    if (checked) {
      setSelectedAssignments((prev) => [...prev, assignment]);
    } else {
      setSelectedAssignments((prev) =>
        prev.filter((prevAssignment) => prevAssignment.id !== assignment.id)
      );
    }
  };

  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedAssignments(results);
    } else {
      setSelectedAssignments([]);
    }
  };

  const isHeaderChecked =
    results.length > 0 &&
    results.every((currentAssignment) =>
      selectedAssignments.some(
        (selectedAssignment) => selectedAssignment.id === currentAssignment.id
      )
    );

  return (
    <div
      style={{
        padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "10px 14px 32px" : "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      {correctionAssignment && (
        <CorrectionModal assignmentId={correctionAssignment.id} onClose={() => setCorrectionAssignment(null)} />
      )}
      {drawingAssignment && (
        <MathDrawingModal
          assignmentId={drawingAssignment.id}
          onClose={() => setDrawingAssignment(null)}
          onMarked={(payload) =>
            setDrawingPendingById((previous) => ({
              ...previous,
              [drawingAssignment.id]: payload.item_list.filter((one) => one.is_submitted && one.is_correct == null).length,
            }))
          }
        />
      )}
      {workPhotoAssignment && (
        <MathWorkPhotoModal
          assignmentId={workPhotoAssignment.id}
          onClose={() => setWorkPhotoAssignment(null)}
          onRated={(rating) => setRatingById((previous) => ({ ...previous, [workPhotoAssignment.id]: rating }))}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">
            학생별 학습 내역
          </h3>
        )}
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div style={{ flex: isMobileW ? 1 : "none", width: isMobileW ? "auto" : "30%", minWidth: isMobileW ? 0 : "220px" }}>
          <WorksheetSearchBar handleFilter={setKeyword} />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: isMobileW ? "column" : "row", // [모바일 수정] 필터를 표 위로
          alignItems: "stretch",
          marginTop: "10px",
        }}
      >
        <FilteringTab
          levels={levels}
          allStudentList={allStudents}
          selectedParams={selectedParams}
          setSelectedParams={setSelectedParams}
        />
        {typeSelect === "leveltest" ? (
          // 레벨테스트는 ECHO 계통이라 과제 표와 열이 다르다. 표를 통째로 갈아끼운다.
          <div style={{ width: isMobileW ? "100%" : "85%", minWidth: 0, backgroundColor: "#FFF", paddingBottom: "5px", borderRadius: "18px", border: "1px solid #E6EDEC", overflow: "hidden" }}>
            <StudentLevelTestHistory
              keyword={keyword}
              studentId={selectedParams?.studentId}
            />
          </div>
        ) : typeSelect === "paper" ? (
          <div style={{ width: isMobileW ? "100%" : "85%", minWidth: 0, backgroundColor: "#FFF", paddingBottom: "5px", borderRadius: "18px", border: "1px solid #E6EDEC", overflow: "hidden" }}>
            <PaperAssignmentListView keyword={keyword} />
          </div>
        ) : (<>
        <div
          style={{
            width: isMobileW ? "100%" : "85%",
            minWidth: 0,
            backgroundColor: "#FFF",
            paddingBottom: "5px",
            borderRadius: "18px",
            border: "1px solid #E6EDEC",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table className="data-table" style={{ minWidth: "900px" }}>
            <colgroup>
              <col style={{ width: "50px" }} />
              <col style={{ width: "80px" }} />
              <col style={{ width: "56px" }} />
              <col />
              <col style={{ width: "70px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "60px" }} />
              <col style={{ width: "55px" }} />
              <col style={{ width: "150px" }} />
            </colgroup>
            <tbody>
              <tr className="data-table__header-row">
                <td className="cell-center">
                  <CustomCheckbox
                    checked={isHeaderChecked}
                    onChange={(e) =>
                      handleHeaderCheckboxChange(e.target.checked)
                    }
                  />
                </td>
                <td className="cell-nowrap">이름</td>
                <td className="cell-center cell-nowrap">링크</td>
                <td className="cell-nowrap" style={{ paddingLeft: "15px" }}>
                  학습지명
                </td>
                <td className="cell-center cell-nowrap">마감일</td>
                <td className="cell-center cell-nowrap">채점</td>
                <td className="cell-center cell-nowrap">해설지</td>
                <td className="cell-center cell-nowrap">복습</td>
                <td className="cell-center cell-nowrap">
                  제출 시간
                </td>
              </tr>
              {results &&
                results.map((item, index) => {
                  // 이름은 응답(`item.student.name`)에 이미 들어 있다. 예전에는
                  // redux 의 학생 목록에서 다시 찾았는데, 그 목록에 없으면
                  // **이름만 빈칸이 되고 링크 복사 버튼은 그대로 남았다.**
                  // 목록이 아직 안 실렸거나 숨긴 학생이면 그렇게 된다.
                  const studentName = item.student?.name || "";
                  const isChecked = selectedAssignments.includes(item);
                  return (
                    <tr key={item.id}>
                      <td
                        style={{
                          textAlign: "center",
                        }}
                      >
                        <CustomCheckbox
                          checked={isChecked}
                          onChange={(e) =>
                            handleCheckboxChange(e.target.checked, item)
                          }
                        />
                      </td>
                      <td className="cell-nowrap">
                        {studentName ? (
                          <a
                            href={showStudentURL(item.student.session)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="학생 페이지 열기"
                            className="cell-link"
                          >
                            {studentName}
                          </a>
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="cell-center">
                        <LinkCopyButton
                          URL={showStudentURL(item.student.session)}
                        />
                      </td>
                      <td>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minHeight: "40px",
                          whiteSpace: "nowrap",
                        }}>
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flexShrink: 1 }}>{item.title}</span>
                        <div style={{ marginLeft: "10px", display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                          <WorksheetTypeBox type={item.type} title={item.title} />
                          {item.is_retry && (
                            <span style={{
                              backgroundColor: "#E5FBF8",
                              color: "#00A396",
                              padding: "4px 10px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              flexShrink: 0
                            }}>
                              복습 {item.retry_info?.retry_count || ''}회차
                            </span>
                          )}
                        </div>
                        </div>
                      </td>
                      <td className="cell-center cell-nowrap">
                        {formatDateString(item.closed_at)}
                      </td>
                      <td>
                        <ReviewPageButton
                          assignment={item}
                          showReviewPage={showReviewPage}
                        />
                      </td>
                      <td className="cell-center">
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                          {item.commentary_pdf_path !== "" && (
                            <button
                              onClick={() => showCommentaryPDF(item)}
                              style={{
                                backgroundColor: "#F4F8F7",
                                border: "1px solid #E6EDEC",
                                borderRadius: "10px",
                                padding: "5px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              title="문서 열기"
                            >
                              <DescriptionIcon
                                style={{
                                  fill: "#000000",
                                  fontSize: "24px",
                                  stroke: "#EBEBEB",
                                }}
                              />
                            </button>
                          )}
                          {(item.drawing_pending_count > 0 || drawingPendingById[item.id] !== undefined) && (() => {
                            const pendingCount = drawingPendingById[item.id] ?? item.drawing_pending_count;
                            return (
                              <button
                                onClick={() => setDrawingAssignment(item)}
                                style={{ ...mathPdfButtonStyle, position: "relative" }}
                                title={pendingCount ? `그리기 채점 ${pendingCount}문제 남음` : "그리기 채점 끝"}
                              >
                                <BrushIcon style={{ ...mathPdfIconStyle, fill: pendingCount ? "#000000" : "#00A396" }} />
                                {pendingCount > 0 && <span style={unseenDotStyle} />}
                              </button>
                            );
                          })()}
                          {item.work_photo?.uploaded && (() => {
                            const rating = ratingById[item.id] ?? item.work_photo.rating;
                            const ratingInfo = WORK_PHOTO_RATING_LIST.find((one) => one.value === rating);
                            return (
                              <button
                                onClick={() => setWorkPhotoAssignment(item)}
                                style={{ ...mathPdfButtonStyle, position: "relative" }}
                                title={ratingInfo ? `풀이 사진 · ${ratingInfo.label}` : "풀이 사진 · 아직 안 봄"}
                              >
                                <PhotoCameraIcon style={{ ...mathPdfIconStyle, fill: ratingInfo ? ratingInfo.color : "#000000" }} />
                                {!ratingInfo && <span style={unseenDotStyle} />}
                              </button>
                            );
                          })()}
                          {item.type === "math_level_test" && item.finished_at && (
                            <button
                              onClick={() => openMathLevelReport(item)}
                              style={mathPdfButtonStyle}
                              title="레벨테스트 리포트"
                            >
                              <AssessmentIcon style={mathPdfIconStyle} />
                            </button>
                          )}
                          {item.type === "math_level_test" && item.finished_at && (
                            <button
                              onClick={() => openMathLevelClassReport(item)}
                              style={mathPdfButtonStyle}
                              title="반별 지침"
                            >
                              <GroupsIcon style={mathPdfIconStyle} />
                            </button>
                          )}
                          {String(item.type || "").startsWith("math_") && item.finished_at && (
                            <>
                              {item.grade !== 100 && (
                                <button
                                  onClick={() => openMathWrongPdf(item, "problem")}
                                  style={mathPdfButtonStyle}
                                  title="오답 문제지 PDF (틀린 문제 다시 풀기)"
                                >
                                  <RefreshIcon style={mathPdfIconStyle} />
                                </button>
                              )}
                              <button
                                onClick={() => openMathWrongPdf(item, "explain")}
                                style={mathPdfButtonStyle}
                                title="해설지 PDF (채점 · 학생 답 · 정답 · 해설)"
                              >
                                <DescriptionIcon style={mathPdfIconStyle} />
                              </button>
                            </>
                          )}
                          {ENGLISH_EXPLAIN_PDF_TYPE_LIST.includes(item.type) && item.finished_at && (
                            <button
                              onClick={() => openEnglishExplainPdf(item)}
                              style={mathPdfButtonStyle}
                              title="채점 해설지 PDF (채점 · 학생 답 · 정답 · 해설)"
                            >
                              <FactCheckIcon style={mathPdfIconStyle} />
                            </button>
                          )}
                          {/* AI 가 매긴 첨삭(리더스북 쪽 위 답 · 원서 서술형)을 선생님이 고친다. 원서는 내용이해 유형이다 */}
                          {["readers_book", "comprehensive"].includes(item.type) && item.finished_at && (
                            <button
                              onClick={() => setCorrectionAssignment(item)}
                              style={mathPdfButtonStyle}
                              title="AI 첨삭 고치기"
                            >
                              <RateReviewIcon style={mathPdfIconStyle} />
                            </button>
                          )}
                          {item.type === "vocabulary" && item.finished_at && item.grade !== 100 && (
                            <>
                              <button
                                onClick={() => handleVocabularyRetryPDF(item)}
                                style={{
                                  backgroundColor: "#F4F8F7",
                                  border: "1px solid #E6EDEC",
                                  borderRadius: "10px",
                                  padding: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                title="단어 재시험지 PDF 다운로드"
                              >
                                <RefreshIcon
                                  style={{
                                    fill: "#000000",
                                    fontSize: "24px",
                                    stroke: "#EBEBEB",
                                  }}
                                />
                              </button>
                              <button
                                onClick={() => handleVocabularyWordbookPDF(item)}
                                style={{
                                  backgroundColor: "#F4F8F7",
                                  border: "1px solid #E6EDEC",
                                  borderRadius: "10px",
                                  padding: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                title="틀린 단어 단어장 PDF 다운로드"
                              >
                                <BookIcon
                                  style={{
                                    fill: "#000000",
                                    fontSize: "24px",
                                    stroke: "#EBEBEB",
                                  }}
                                />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="cell-center">
                        {(RETRY_SUPPORTED_TYPE_LIST.includes(item.type) && item.finished_at && ((!item.is_retry) ||
                          (item.is_retry
                            // && item.retry_info?.retry_count < item.retry_info?.max_retries
                          ))) && (
                            <button
                              onClick={() => handleRetryAssignment(item)}
                              style={{
                                backgroundColor: item.retry_info?.retry_assignment_id ? "#E6EDEC" : "#0E141B",
                                color: item.retry_info?.retry_assignment_id ? "#7A8490" : "#FFF",
                                border: "none",
                                borderRadius: "999px",
                                padding: "6px 14px",
                                cursor: item.retry_info?.retry_assignment_id ? "default" : "pointer",
                                fontSize: "13px",
                                fontWeight: 700,
                                fontFamily: "inherit",
                                transition: "background-color .12s",
                              }}
                              onMouseEnter={(e) => {
                                if (!item.retry_info?.retry_assignment_id)
                                  e.currentTarget.style.backgroundColor = "#00A396";
                              }}
                              onMouseLeave={(e) => {
                                if (!item.retry_info?.retry_assignment_id)
                                  e.currentTarget.style.backgroundColor = "#0E141B";
                              }}
                              disabled={item.retry_info?.retry_assignment_id}
                            >
                              {item.retry_info?.retry_assignment_id ? "출제됨" : "출제"}
                            </button>
                          )}
                      </td>
                      <td className="cell-center">
                        <SubmittedTime value={item.finished_at} />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          <Pagination
            visiblePages={visiblePages}
            current_page={current_page}
            handlePageClick={handlePageClick}
          />
        </div>
        {selectedAssignments.length > 0 && (
          <DeleteWorksheetModal
            worksheets={selectedAssignments}
            setSelectedWorksheets={setSelectedAssignments}
            type={"assignment"}
            onDeleted={() => {
              const typeParam = typeSelect === "all" ? null : typeSelect;
              dispatch(
                fetchAssignments({
                  page: current_page,
                  type: typeParam,
                  keyword: keyword,
                  levelId: selectedParams.levelId,
                  studentId: selectedParams.studentId,
                  studentGrade: selectedParams.studentGrade,
                })
              );
            }}
          />
        )}
        </>)}
      </div>
    </div>
  );
};

// 채점 해설지를 여는 영어 과제 종류. 서버 api/assignment/english_pdf.py 의 WORKSHEET_TYPE_NAME 과 같다
const ENGLISH_EXPLAIN_PDF_TYPE_LIST = ["paragraph", "grammar", "comprehensive", "sentence", "vocabulary", "bank", "grammar_bank", "analysis", "readers_book"];

const mathPdfButtonStyle = {
  backgroundColor: "#F4F8F7",
  border: "1px solid #E6EDEC",
  borderRadius: "10px",
  padding: "5px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};
const mathPdfIconStyle = { fill: "#000000", fontSize: "24px", stroke: "#EBEBEB" };
// 아직 정성을 안 매긴 풀이 사진에 붙는 점
const unseenDotStyle = {
  position: "absolute", top: "3px", right: "3px", width: "8px", height: "8px", borderRadius: "50%", background: "#FF5A7A",
};

export default AssignmentReviewTable;
